import axios from 'axios';
import { getToken, clearAuth } from '../utils/authToken';
import { reportApiFailure } from '../utils/demoIssues';

// ─────────────────────────────────────────
// BASE CONFIGURATION
// ─────────────────────────────────────────
const API = axios.create({
  baseURL     : process.env.REACT_APP_API_URL || 'http://localhost:8000',
  withCredentials: true,
  timeout     : 15000,  // FE-F2: 15s timeout — Render cold-starts take ~30s but
  // API calls should respond within 15s once the server is warm. On timeout
  // the interceptor's error.code will be 'ECONNABORTED' — treated as network
  // error (not 401) so it won't trigger authLogout.
});

// Flag used to suppress login-redirect for silent background reloads.
let _isSilentReload = false;
export const setSilentReload = (val) => { _isSilentReload = val; };

// ── Cold-start detection (ONBOARD-2) ─────────────────────────────────────────
// Fires 'serverWaking' when a timeout / network error is detected (Render cold
// start takes ~30s on the free tier). Fires 'serverAwake' on the next success.
// The WakingUpOverlay component listens for these and shows/hides accordingly.
let _serverWakingFired = false;
let _suppressWaking    = false;
export const setSuppressWaking = (val) => { _suppressWaking = val; };
const _dispatchWaking = () => {
  if (_serverWakingFired || _suppressWaking) return;
  _serverWakingFired = true;
  window.dispatchEvent(new Event('serverWaking'));
};
const _dispatchAwake = () => {
  if (!_serverWakingFired) return;
  _serverWakingFired = false;
  window.dispatchEvent(new Event('serverAwake'));
};
// ─────────────────────────────────────────────────────────────────────────────

// Flag to suppress authLogout dispatch during the login bootstrap sequence.
// A 401 on /me/ right after login is a cookie-timing race, NOT an expired session.
let _isLoggingIn = false;

// Counter: how many consecutive /me/ 401s we've seen for the current session.
// Platform admins need two consecutive /me/ 401s before we accept that the
// session has truly expired — this filters out transient Render cold-start errors.
let _meFailStreak = 0;

// Helper: read is_platform_admin from the cached user object in localStorage.
// Returns false if there is no cached user or JSON parse fails.
const _isPlatformAdmin = () => {
  try {
    const raw = window.localStorage.getItem('user');
    if (!raw) return false;
    return Boolean(JSON.parse(raw).is_platform_admin);
  } catch {
    return false;
  }
};

API.interceptors.request.use((config) => {
  // Normal sessions: httpOnly cookie 'st_token' is sent automatically by the browser.
  // Impersonation sessions: token lives in sessionStorage and must be set as Bearer.
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (response) => {
    // Successful /me/ response — reset the consecutive-failure streak.
    const url = response.config?.url || '';
    if (url === '/me/' || url.endsWith('/me/')) _meFailStreak = 0;
    // Server is awake — dismiss the cold-start overlay if it was shown.
    _dispatchAwake();
    return response;
  },
  (error) => {
    // D255: in a demo session, report failed calls/timeouts (no-op otherwise).
    reportApiFailure(error);
    if (_isSilentReload) {
      return Promise.reject(error);
    }
    // During login flow, never fire authLogout — 401 here is a login failure,
    // not an expired session. Let the caller handle it.
    if (_isLoggingIn) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const failingUrl   = error.config?.url || '';
      const hasImpToken  = Boolean(window.sessionStorage.getItem('__active_impersonate_token'));
      const hasLocalUser = Boolean(window.localStorage.getItem('user'));
      const isPlatAdmin  = _isPlatformAdmin();

      // Exact-match check for the session endpoint — must be /me/ exactly,
      // not a URL that merely contains the string "me" (e.g. /members/).
      const isExactlyMeUrl = (failingUrl === '/me/' || failingUrl.endsWith('/me/'));
      const isLoginUrl     = failingUrl.includes('/login/');
      const isSessionUrl   = isExactlyMeUrl || isLoginUrl;

      if (hasImpToken) {
        // ── Impersonation tab ─────────────────────────────────────────────
        // Any 401 = the 2-hour impersonation token has expired. Log out the tab.
        clearAuth();
        window.dispatchEvent(new CustomEvent('authLogout', { detail: { reason: 'expired' } }));

      } else if (isPlatAdmin && isExactlyMeUrl) {
        // ── Platform admin — /me/ returned 401 ───────────────────────────
        // Platform admins genuinely do have long sessions (24h cookie).
        // A single /me/ 401 can be a transient Render cold-start 502 that
        // looks like a 401 or a brief DB connection blip. Require TWO
        // consecutive /me/ 401s before treating it as a real session expiry.
        _meFailStreak++;
        if (_meFailStreak >= 2) {
          _meFailStreak = 0;
          clearAuth();
          window.dispatchEvent(new CustomEvent('authLogout', { detail: { reason: 'expired' } }));
        }
        // First failure: silently reject so the caller shows its own error.

      } else if (!isPlatAdmin && isSessionUrl && hasLocalUser) {
        // ── Normal club admin / member — /me/ or /login/ returned 401 ────
        // For regular users, a single /me/ 401 is definitive evidence that
        // the session cookie has expired. Log them out immediately.
        clearAuth();
        window.dispatchEvent(new CustomEvent('authLogout', { detail: { reason: 'expired' } }));

      }
      // All other 401s (club-scoped endpoints, wrong role, etc.) are rejected
      // silently — the individual page component shows its own error state.
    }

    if (!error.response && error.code !== 'ERR_CANCELED' && getToken()) {
      // Network error in an impersonation tab (getToken() reads sessionStorage;
      // returns non-null only when an impersonation token is present).
      const url    = error.config?.url || '';
      const method = (error.config?.method || '').toLowerCase();
      const isGetAuthEndpoint = method === 'get' &&
        ['/clubs/', '/me/'].some(e => url.includes(e));
      if (isGetAuthEndpoint) {
        clearAuth();
        window.dispatchEvent(new CustomEvent('authLogout', { detail: { reason: 'expired' } }));
      }
    }

    // ── Cold-start detection ──────────────────────────────────────────────────
    // ECONNABORTED = axios timeout (15s). No error.response = network error
    // (server offline or Render still booting). Both indicate a cold start.
    // Only trigger for non-silent, non-login requests that aren't 4xx errors.
    const isColdStartSignal =
      !_isSilentReload &&
      !_isLoggingIn &&
      (error.code === 'ECONNABORTED' || (!error.response && error.code !== 'ERR_CANCELED'));
    if (isColdStartSignal) _dispatchWaking();
    // ─────────────────────────────────────────────────────────────────────────

    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────
// ERROR HELPER
// ─────────────────────────────────────────
export const formatApiError = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(e => {
        const field = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : '';
        return field ? `${field}: ${e.msg}` : e.msg;
      })
      .join(' | ');
  }
  try { return JSON.stringify(detail); } catch { return fallback; }
};

// ─────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────

/**
 * loginAndGetMe — performs the full login bootstrap atomically.
 *
 * Cross-origin issue: after /login/ sets the httpOnly cookie, the browser
 * may not have committed it to its cookie jar before the immediately-following
 * /me/ request fires. This causes /me/ to return 401, which the interceptor
 * used to misread as "session expired" and fire authLogout.
 *
 * Fix: pass the token returned by /login/ directly as a Bearer header for the
 * /me/ bootstrap call. The cookie is still set and will be used for all
 * subsequent requests — this is only needed for this one call.
 *
 * The _isLoggingIn flag also suppresses the authLogout interceptor during
 * this whole sequence so any failure surfaces as a thrown error to the caller.
 */
export const loginAndGetMe = async (email, password, timeoutMs = 15000) => {
  _isLoggingIn = true;
  _meFailStreak = 0;   // fresh login — reset failure streak
  try {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    const loginRes = await API.post('/login/', formData, { timeout: timeoutMs });
    const bootstrapToken = loginRes.data?.access_token;

    // Use the token from the response body as a Bearer header for this one call.
    // This sidesteps the cross-origin httpOnly cookie timing race entirely.
    const meRes = await API.get('/me/', {
      timeout: timeoutMs,
      headers: bootstrapToken
        ? { Authorization: `Bearer ${bootstrapToken}` }
        : {},
    });
    return meRes.data;
  } finally {
    _isLoggingIn = false;
  }
};

// Keep individual exports for backward compat with other callers
export const login = async (email, password) => {
  const formData = new FormData();
  formData.append('username', email);
  formData.append('password', password);
  const response = await API.post('/login/', formData);
  return response.data;
};

export const getMe = async () => {
  const response = await API.get('/me/');
  return response.data;
};

export const changePassword = async (data) => {
  const response = await API.post('/change-password/', data);
  return response.data;
};

export const register = async (data) => {
  const response = await API.post('/register/', data);
  return response.data;
};

// ─────────────────────────────────────────
// NOTIFICATIONS
// ─────────────────────────────────────────
export const getNotifications = async () => {
  const response = await API.get('/notifications/');
  return response.data;
};

export const markNotificationRead = async (id) => {
  const response = await API.post(`/notifications/${id}/read/`);
  return response.data;
};

// ─────────────────────────────────────────
// CLUB
// ─────────────────────────────────────────
export const createClub = async (data) => {
  const response = await API.post('/clubs/', data);
  return response.data;
};

export const logoutApi = async () => {
  try { await API.post('/logout/'); } catch { /* silent */ }
};

export const getClub = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/`);
  return response.data;
};

export const getClubLogo = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/logo/`);
  return response.data.logo_url;
};

export const uploadLogo = async (clubId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await API.post(`/clubs/${clubId}/upload-logo/`, formData);
  window.dispatchEvent(new CustomEvent('logoUpdated', { detail: { clubId, logo_url: response.data.logo_url } }));
  return response.data;
};

export const getClubAgeGroups = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/age-groups/`);
  return response.data;
};
export const createClubAgeGroup = async (clubId, data) => {
  const response = await API.post(`/clubs/${clubId}/age-groups/`, data);
  return response.data;
};
export const updateClubAgeGroup = async (clubId, groupId, data) => {
  const response = await API.put(`/clubs/${clubId}/age-groups/${groupId}/`, data);
  return response.data;
};
export const deleteClubAgeGroup = async (clubId, groupId) => {
  const response = await API.delete(`/clubs/${clubId}/age-groups/${groupId}/`);
  return response.data;
};

// ─────────────────────────────────────────
// SQUADS
// ─────────────────────────────────────────
export const getSquads = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/squads/`);
  return response.data;
};

export const createSquad = async (data) => {
  const response = await API.post('/squads/', data);
  return response.data;
};

export const updateSquad = async (squadId, data) => {
  const response = await API.put(`/squads/${squadId}/`, data);
  return response.data;
};

export const deactivateSquad = async (id) => {
  const response = await API.post('/squads/deactivate/', { id });
  return response.data;
};

// ─────────────────────────────────────────
// MEMBERS
// ─────────────────────────────────────────
export const getMembers = async (clubId, includeArchived = false) => {
  const response = await API.get(`/clubs/${clubId}/members/?include_archived=${includeArchived}`);
  return response.data;
};

export const createMember = async (data) => {
  const response = await API.post('/members/', data);
  return response.data;
};

export const getMember = async (memberId) => {
  const response = await API.get(`/members/${memberId}/`);
  return response.data;
};

export const updateMember = async (memberId, data) => {
  const response = await API.put(`/members/${memberId}/`, data);
  return response.data;
};

export const archiveMember = async (memberId) => {
  const response = await API.post(`/members/${memberId}/archive/`);
  return response.data;
};

export const restoreMember = async (memberId) => {
  const response = await API.post(`/members/${memberId}/restore/`);
  return response.data;
};

export const deleteMember = async (memberId) => {
  const response = await API.delete(`/members/${memberId}/`);
  return response.data;
};

export const getMemberHistory = async (memberId) => {
  const response = await API.get(`/members/${memberId}/history/`);
  return response.data;
};

export const getMemberPersonalBests = async (memberId) => {
  const response = await API.get(`/members/${memberId}/personal-bests/`);
  return response.data;
};

export const getMemberAttendance = async (memberId) => {
  const response = await API.get(`/members/${memberId}/attendance/`);
  return response.data;
};

export const getMemberEntries = async (memberId) => {
  const response = await API.get(`/members/${memberId}/entries/`);
  return response.data;
};

// ─────────────────────────────────────────
// RACE CATEGORIES
// ─────────────────────────────────────────
export const getRaceCategories = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/race-categories/`);
  return response.data;
};

export const createRaceCategory = async (data) => {
  const response = await API.post('/race-categories/', data);
  return response.data;
};

export const updateRaceCategory = async (id, data) => {
  const response = await API.put(`/race-categories/${id}/`, data);
  return response.data;
};

export const deactivateRaceCategory = async (id) => {
  const response = await API.post('/race-categories/deactivate/', { id });
  return response.data;
};

// D172: 25m-substitution advisory check (DECISIONS.md D149/D171) -- read-only,
// tells the caller whether this swimmer's recent time at the next-shorter
// distance suggests 25m would suit them better than the rostered 50m/100m
// race. Only meaningful for those two distances -- the backend itself
// returns substitution: null for anything else or when no rule/reference
// time applies, so callers can call this unconditionally for 50m/100m
// categories without needing to duplicate the rule logic client-side.
export const getSubstitutionCheck = (categoryId, memberId) =>
  API.get(`/race-categories/${categoryId}/substitution-check/?member_id=${memberId}`).then(r => r.data);

// ─────────────────────────────────────────
// EVENTS
// ─────────────────────────────────────────
export const getEvents = async (clubId, eventType = null) => {
  const params = eventType ? `?event_type=${eventType}` : '';
  const response = await API.get(`/clubs/${clubId}/events/${params}`);
  return response.data;
};

export const createEvent = async (data) => {
  const response = await API.post('/events/', data);
  return response.data;
};

export const getEventRaceCategories = async (eventId) => {
  const response = await API.get(`/events/${eventId}/race-categories/`);
  return response.data;
};

export const addRaceCategoryToEvent = async (data) => {
  const response = await API.post('/events/add-race-category/', data);
  return response.data;
};

export const getEligibleSwimmers = (categoryId, eventId) =>
  API.get(`/race-categories/${categoryId}/eligible-swimmers/?event_id=${eventId}`).then(r => r.data);

export const getEventEntries = async (eventId) => {
  const response = await API.get(`/events/${eventId}/entries/`);
  return response.data;
};

export const getEventResults = async (eventId) => {
  const response = await API.get(`/events/${eventId}/results/`);
  return response.data;
};

export const getEventHeats = async (eventId) => {
  const response = await API.get(`/events/${eventId}/heats/`);
  return response.data;
};

export const getEventAttendance = async (eventId) => {
  const response = await API.get(`/events/${eventId}/attendance/`);
  return response.data;
};

export const getEventCheckins = async (eventId) => {
  const response = await API.get(`/events/${eventId}/checkins/`);
  return response.data;
};

export const getMyCheckinStatus = async (eventId) => {
  const response = await API.get(`/events/${eventId}/my-checkin/`);
  return response.data;
};

export const getEventLaneAssignments = async (eventId) => {
  const response = await API.get(`/events/${eventId}/lane-assignments/`);
  return response.data;
};

// ─────────────────────────────────────────
// ENTRIES
// ─────────────────────────────────────────
export const submitEntry = async (data) => {
  const response = await API.post('/entries/', data);
  return response.data;
};

export const updateEntryStatus = async (data) => {
  const response = await API.post('/entries/update-status/', data);
  return response.data;
};

// ─────────────────────────────────────────
// SWIMMER EVENT ENTRY (member-facing)
// ─────────────────────────────────────────
export const getEventsOpenForEntry = async () => {
  const response = await API.get('/events/open-for-entry/');
  return response.data;
};

export const getMyEntries = async () => {
  const response = await API.get('/events/my-entries/');
  return response.data;
};

export const getMyEntriesAll = async () => {
  const response = await API.get('/events/my-entries-all/');
  return response.data;
};

export const getMyPastEvents = async () => {
  const response = await API.get('/events/my-past-events/');
  return response.data;
};

export const withdrawEntry = async (entryId) => {
  const response = await API.delete(`/entries/${entryId}/`);
  return response.data;
};

export const deleteEntry = async (entryId) => {
  const response = await API.delete(`/entries/${entryId}/admin/`);
  return response.data;
};

// ─────────────────────────────────────────
// HEATS
// ─────────────────────────────────────────
export const generateHeats = async (data) => {
  const response = await API.post('/heats/generate/', data);
  return response.data;
};

export const activateHeat = async (heatId) => {
  const response = await API.post(`/heats/${heatId}/activate/`);
  return response.data;
};

export const completeHeat = async (heatId) => {
  const response = await API.post(`/heats/${heatId}/complete/`);
  return response.data;
};

export const markDNS = async (heatId, data) => {
  const response = await API.post(`/heats/${heatId}/mark-dns/`, data);
  return response.data;
};

export const markDQ = async (heatId, data) => {
  const response = await API.post(`/heats/${heatId}/mark-dq/`, data);
  return response.data;
};

export const markScratch = async (heatId, data) => {
  const response = await API.post(`/heats/${heatId}/mark-scratch/`, data);
  return response.data;
};

export const reopenHeat = async (heatId) => {
  const response = await API.post(`/heats/${heatId}/reopen/`);
  return response.data;
};

export const clearLaneStatus = async (heatId, data) => {
  const response = await API.post(`/heats/${heatId}/clear-lane-status/`, data);
  return response.data;
};

export const notifyTimekeepersForHeat = async (heatId) => {
  const response = await API.post(`/heats/${heatId}/notify-timekeepers/`);
  return response.data;
};

export const notifySwimmersForHeat = async (heatId) => {
  const response = await API.post(`/heats/${heatId}/notify-swimmers/`);
  return response.data;
};

// ─────────────────────────────────────────
// LANE ASSIGNMENTS
// ─────────────────────────────────────────
export const clearLaneAssignments = async (eventId) => {
  const response = await API.post(`/events/clear-lane-assignments/?event_id=${eventId}`);
  return response.data;
};

export const assignEventLane = async (data) => {
  const response = await API.post('/events/assign-lanes/', data);
  return response.data;
};

export const autoAssignLanes = async (eventId) => {
  const response = await API.post(`/events/auto-assign-lanes/?event_id=${eventId}`);
  return response.data;
};

export const checkinToEvent = async (eventId) => {
  const response = await API.post(`/events/${eventId}/checkin/`);
  return response.data;
};

// ─────────────────────────────────────────
// RESULTS
// ─────────────────────────────────────────
export const recordResult = async (data) => {
  const response = await API.post('/results/', data);
  return response.data;
};

// ─────────────────────────────────────────
// LEADERBOARD
// ─────────────────────────────────────────
export const getLeaderboard = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/leaderboard/`);
  return response.data;
};

export const getTrophyEligibility = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/trophy-eligibility/`);
  return response.data;
};

// ─────────────────────────────────────────
// ATTENDANCE
// ─────────────────────────────────────────
export const markAttendance = async (data) => {
  const response = await API.post('/attendance/', data);
  return response.data;
};

// ─────────────────────────────────────────
// ADMIN
// ─────────────────────────────────────────
export const getPendingUsers = async (clubId = null) => {
  const params = clubId ? `?club_id=${clubId}` : '';
  const response = await API.get(`/admin/pending-users/${params}`);
  return response.data;
};

export const decideUser = async (data) => {
  const response = await API.post('/admin/decide-user/', data);
  return response.data;
};

export const getAllUsers = async (clubId = null) => {
  const params = clubId ? `?club_id=${clubId}` : '';
  const response = await API.get(`/admin/all-users/${params}`);
  return response.data;
};

export const promoteToAdmin = async (data) => {
  const response = await API.post('/admin/promote-to-admin/', data);
  return response.data;
};

export const revokeAdmin = async (data) => {
  const response = await API.post('/admin/revoke-admin/', data);
  return response.data;
};

export const updateUserRole = async (data) => {
  const response = await API.post('/admin/update-user-role/', data);
  return response.data;
};

export const reactivateUser = async (data) => {
  const response = await API.post('/admin/reactivate-user/', data);
  return response.data;
};

export const createMemberFromUser = async (data) => {
  const response = await API.post('/admin/create-member-from-user/', data);
  return response.data;
};

export const createUserAsAdmin = async (data) => {
  const response = await API.post('/admin/create-user/', data);
  return response.data;
};

// Session 92: read-only dry-run check used by the Add User two-step flow --
// tells the UI whether name/email/phone/DOB match an existing member BEFORE
// creating the login, so role checkboxes only show up when a brand-new
// member is actually about to be created.
export const checkMemberMatch = async (data) => {
  const response = await API.post('/admin/check-member-match/', data);
  return response.data;
};

// Session 90 recovery action: link an approved user with no member profile
// (or whose auto-match missed an existing one) to an existing Member record,
// instead of creating a duplicate via createMemberFromUser.
export const linkUserToMember = async (data) => {
  const response = await API.post('/admin/link-user-member/', data);
  return response.data;
};

// Session 90 (round 2) recovery action: detach a user's login from a WRONGLY
// linked member profile without deleting either record, so the duplicate
// member can be deleted safely and the user re-linked to the correct one.
export const unlinkUserFromMember = async (data) => {
  const response = await API.post('/admin/unlink-user-member/', data);
  return response.data;
};

export const deleteUser = async (userId, archiveSwimmer = false) => {
  const response = await API.delete(`/admin/users/${userId}/?archive_swimmer=${archiveSwimmer}`);
  return response.data;
};

// (getAuditLogs is defined at the bottom of this file — club-scoped, paginated)

// ─────────────────────────────────────────
// EVENTS — Additional
// ─────────────────────────────────────────
export const createRecurringEvents = async (data) => {
  const response = await API.post('/events/recurring/', data);
  return response.data;
};

export const cancelEvent = async (data) => {
  const response = await API.post('/events/cancel/', data);
  return response.data;
};

export const reinstateEvent = async (data) => {
  const response = await API.post('/events/reinstate/', data);
  return response.data;
};

export const getEventDetail = async (eventId) => {
  const response = await API.get(`/events/${eventId}/detail/`);
  return response.data;
};

export const toggleRegistration = async (eventId, open) => {
  const response = await API.post(`/events/toggle-registration/?event_id=${eventId}&open=${open}`);
  return response.data;
};

export const closeRegistration = async (data) => {
  const response = await API.post('/events/close-registration/', data);
  return response.data;
};

export const updateDeadline = async (data) => {
  const response = await API.post('/events/update-deadline/', data);
  return response.data;
};

// ─────────────────────────────────────────
// RACE DAY
// ─────────────────────────────────────────
export const getTimekeepers = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/timekeepers/`);
  return response.data;
};

export const getTimekeeperMembers = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/timekeeper-members/`);
  return response.data;
};

export const adminManualCheckin = async (eventId, memberId, userId) => {
  const response = await API.post(
    `/events/${eventId}/manual-checkin/`,
    { event_id: eventId, member_id: memberId || null, user_id: userId || null }
  );
  return response.data;
};

export const checkoutFromEvent = async (eventId) => {
  const response = await API.post(`/events/${eventId}/checkout/`);
  return response.data;
};

export const adminForceCheckout = async (eventId, userId) => {
  const response = await API.post(`/events/${eventId}/force-checkout/${userId}/`);
  return response.data;
};

// AGE GROUPS — use getClubAgeGroups / createClubAgeGroup / updateClubAgeGroup / deleteClubAgeGroup above
// Aliases kept for backwards compatibility with Events.jsx and Members.jsx
export const getAgeGroups    = getClubAgeGroups;
export const createAgeGroup  = createClubAgeGroup;
export const updateAgeGroup  = updateClubAgeGroup;
export const deleteAgeGroup  = deleteClubAgeGroup;

// ─────────────────────────────────────────
// CHAMPIONSHIPS
// ─────────────────────────────────────────
export const getChampionships = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/championships/`);
  return response.data;
};

export const createChampionship = async (clubId, data) => {
  const response = await API.post(`/clubs/${clubId}/championships/`, data);
  return response.data;
};

export const updateChampionship = async (clubId, champId, data) => {
  const response = await API.put(`/clubs/${clubId}/championships/${champId}/`, data);
  return response.data;
};

export const deleteChampionship = async (clubId, champId) => {
  const response = await API.delete(`/clubs/${clubId}/championships/${champId}/`);
  return response.data;
};

export const getChampionshipSlots = async (clubId, champId) => {
  const response = await API.get(`/clubs/${clubId}/championships/${champId}/slots/`);
  return response.data;
};

export const createRaceCategoryFromSlot = async (data) => {
  const response = await API.post('/race-categories/from-championship-slot/', data);
  return response.data;
};

// ─────────────────────────────────────────
// CHAMPIONSHIP WEEKS
// ─────────────────────────────────────────
export const getChampionshipWeeks = async (clubId, champId) => {
  const response = await API.get(`/clubs/${clubId}/championships/${champId}/weeks/`);
  return response.data;
};

export const linkChampionshipWeek = async (clubId, champId, data) => {
  const response = await API.post(`/clubs/${clubId}/championships/${champId}/weeks/`, data);
  return response.data;
};

export const unlinkChampionshipWeek = async (clubId, champId, weekId) => {
  const response = await API.delete(`/clubs/${clubId}/championships/${champId}/weeks/${weekId}/`);
  return response.data;
};

export const getAgeChampionLeaderboard = async (clubId, champId) => {
  const response = await API.get(`/clubs/${clubId}/championships/${champId}/leaderboard/`);
  return response.data;
};

// ─────────────────────────────────────────
// PARENT-CHILD
// ─────────────────────────────────────────
export const getMyChildren = async () => {
  const response = await API.get('/parent/my-children/');
  return response.data;
};

export const getEventsForChild = async (memberId) => {
  const response = await API.get(`/parent/events-for-child/${memberId}/`);
  return response.data;
};

export const parentEnterChild = async (data) => {
  const response = await API.post('/parent/enter-child/', data);
  return response.data;
};

export const parentWithdrawChild = async (entryId) => {
  const response = await API.delete(`/parent/withdraw-child/${entryId}/`);
  return response.data;
};

export const getChildEntries = async (memberId) => {
  const response = await API.get(`/parent/child-entries/${memberId}/`);
  return response.data;
};

// Session 101: past events for a linked child, same shape as getMyPastEvents
// but scoped to member_id -- used by EnterEvents.jsx's "Entering for" child
// selector so the Past tab shows real data for a child, not an empty list.
export const getPastEventsForChild = async (memberId) => {
  const response = await API.get(`/parent/past-events-for-child/${memberId}/`);
  return response.data;
};

export const linkParentToChild = async (data) => {
  const response = await API.post('/admin/parent-children/', data);
  return response.data;
};

export const unlinkParentFromChild = async (linkId) => {
  const response = await API.delete(`/admin/parent-children/${linkId}/`);
  return response.data;
};

export const getAllParentChildLinks = async (clubId = null) => {
  const params = clubId ? `?club_id=${clubId}` : '';
  const response = await API.get(`/admin/parent-children/${params}`);
  return response.data;
};

// ─────────────────────────────────────────
// RACE TEMPLATES
// ─────────────────────────────────────────
export const getRaceTemplates = async (clubId) => {
  const response = await API.get(`/clubs/${clubId}/race-templates/`);
  return response.data;
};

export const createRaceTemplate = async (clubId, data) => {
  const response = await API.post(`/clubs/${clubId}/race-templates/`, data);
  return response.data;
};

export const deleteRaceTemplate = async (clubId, templateId) => {
  const response = await API.delete(`/clubs/${clubId}/race-templates/${templateId}/`);
  return response.data;
};

export const updateRaceTemplate = async (clubId, templateId, data) => {
  const response = await API.put(`/clubs/${clubId}/race-templates/${templateId}/`, data);
  return response.data;
};

export const bulkScheduleEvents = async (data) => {
  const response = await API.post('/events/bulk-schedule/', data);
  return response.data;
};

// ─────────────────────────────────────────
// EXPORTS / DATA BACKUP
// ─────────────────────────────────────────
/**
 * downloadExportBlob — fetches a CSV export from the backend and triggers
 * a browser download. Uses axios blob responseType so the auth cookie is
 * sent automatically (can't use a plain <a href> for credentialed requests).
 *
 * @param {string} type     - export type matching the backend route segment
 *                            e.g. 'members', 'results', 'participation',
 *                            'points', 'attendance', 'club-records'
 * @param {object} params   - optional query params (season_id, date_from, etc.)
 * @param {string} filename - suggested download filename (auto-generated if omitted)
 */
// D89 follow-up: the backend already computes the correct filename (via
// _club_slug -- real club name, Content-Disposition header) for every one
// of these exports. This helper used to ignore that header entirely and
// always use its own client-guessed `filename` argument -- which for the
// two PDF buttons (club-records-pdf, points-card-pdf) is built from a
// `clubName` React state that's fetched asynchronously on mount, so a
// click that lands before that fetch resolves downloads a file named
// "lapistics_club_....pdf" even though the PDF's own content and the
// server's suggested filename are both correct. Confirmed live: response
// header read back as
// `content-disposition: attachment; filename=lapistics_lapistics_play_ground_points_cards_2026-08-10.pdf`
// while the browser saved it as the generic fallback name. Fix: parse the
// server's Content-Disposition header and prefer it; the `filename`
// argument is now only a fallback for the rare case the header is missing.
//
// D91: none of the export/PDF callers (ExportTab.jsx, ClubRecords.jsx, the
// heat-sheet button in Events.jsx) ever included club_id in params, despite
// the backend's _club_id_for_user() docstring assuming ExportTab always
// sent it. This is invisible for normal club admins (backend infers club_id
// from their session) but every export 400s for a platform admin browsing
// a club via PlatformAdminClubSelector -- confirmed live:
// "No club context. If you are a platform admin, select a club first or
// pass ?club_id=N." on /exports/points-card-pdf/. Fixed once here rather
// than at each of the ~6 call sites: AuthContext.jsx already tracks the
// platform admin's selected club in localStorage['selected_club_id'], so
// pull it in automatically whenever the caller hasn't already set club_id
// explicitly. No-op for normal users (that key is only ever set for
// platform admins, and is cleared on login/logout).
export const downloadExportBlob = async (type, params = {}, filename) => {
  const mergedParams = { ...params };
  if (mergedParams.club_id == null) {
    const selectedClubId = window.localStorage.getItem('selected_club_id');
    if (selectedClubId) mergedParams.club_id = selectedClubId;
  }
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(mergedParams).filter(([, v]) => v != null && v !== ''))
  ).toString();
  const url = `/exports/${type}/${qs ? '?' + qs : ''}`;
  const res = await API.get(url, { responseType: 'blob' });
  const disposition = res.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const serverFilename = match ? match[1].trim() : null;
  const objectUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = serverFilename || filename || `lapistics_${type}_${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

/**
 * downloadFullBackup — triggers the zip backup download.
 * Uses blob responseType so auth cookie is sent.
 * D91: same club_id fix as downloadExportBlob above — this hits the same
 * /exports/ router and needs club_id for platform-admin sessions.
 * D162: this endpoint builds 10 CSVs (incl. the Season Awards CSV added in
 * D161, which runs the same _compute_club_champion/_compute_most_improved/
 * _compute_best_attendance/_compute_trophy_eligible/_compute_age_champion
 * functions the on-screen Season Awards page uses -- real per-swimmer
 * computation, not just a query) and zips them, so it legitimately takes
 * longer than the global 15s axios default sized for ordinary CSV/JSON
 * calls. Confirmed live: hit "timeout of 15000ms exceeded" on this club's
 * demo data. Override just this call's timeout, same pattern already used
 * by loginAndGetMe's timeoutMs param above -- 60s gives real headroom
 * without masking a genuinely broken/hanging request forever.
 */
export const downloadFullBackup = async (params = {}) => {
  const mergedParams = { ...params };
  if (mergedParams.club_id == null) {
    const selectedClubId = window.localStorage.getItem('selected_club_id');
    if (selectedClubId) mergedParams.club_id = selectedClubId;
  }
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(mergedParams).filter(([, v]) => v != null && v !== ''))
  ).toString();
  const url = `/exports/full-backup/${qs ? '?' + qs : ''}`;
  const res = await API.get(url, { responseType: 'blob', timeout: 60000 });
  const objectUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `lapistics_full_backup_${new Date().toISOString().slice(0,10)}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

/**
 * downloadSwimRecordPdf — a swimmer's own personal-bests + recent-results +
 * attendance-summary record as one branded PDF. Available to the swimmer
 * themselves, a linked parent, or club admin/coach. See
 * backend/routers/members.py::get_swim_record_pdf.
 */
export const downloadSwimRecordPdf = async (memberId, memberName) => {
  const slug = (memberName || 'swimmer').toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 30);
  const res = await API.get(`/members/${memberId}/swim-record-pdf/`, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `lapistics_${slug}_swim_record.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

/**
 * downloadHeatSheetPdf — printable PDF heat sheet for one event (lane draw
 * by category/heat, HANDICAP categories also show Start Order + Offset).
 * See backend/routers/exports.py::export_heat_sheet_pdf. Thin wrapper around
 * downloadExportBlob so it gets a sensible .pdf filename instead of the
 * generic .csv default.
 */
export const downloadHeatSheetPdf = async (eventId, eventName) => {
  const slug = (eventName || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40);
  await downloadExportBlob(
    'heat-sheet-pdf',
    { event_id: eventId },
    `lapistics_${slug}_heatsheet.pdf`
  );
};

/**
 * downloadClubRecordsPdf — noticeboard/trophy-cabinet-style PDF of current
 * club records, grouped by distance+stroke (same grouping ClubRecords.jsx
 * shows on screen). See backend/routers/exports.py::export_club_records_pdf
 * (D13). Thin wrapper around downloadExportBlob, same pattern as
 * downloadHeatSheetPdf above.
 */
export const downloadClubRecordsPdf = async (clubName) => {
  const slug = (clubName || 'club').toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40);
  await downloadExportBlob(
    'club-records-pdf',
    {},
    `lapistics_${slug}_club_records.pdf`
  );
};

/**
 * downloadPointsCardPdf — printable Point Score Card(s), one page per
 * swimmer (date rows x stroke/distance columns, running Sum/Total),
 * matching the traditional paper/Word/Excel card. See DECISIONS.md D83/D88
 * and backend/routers/exports.py::export_points_card_pdf. Thin wrapper
 * around downloadExportBlob, same pattern as downloadHeatSheetPdf and
 * downloadClubRecordsPdf above.
 *
 * @param {string} clubName  - for the filename slug
 * @param {object} opts      - { seasonId, memberId, squadId } — all optional.
 *                              memberId given -> single-swimmer PDF;
 *                              omitted -> every active swimmer in the club
 *                              (optionally narrowed by squadId), one page each.
 */
export const downloadPointsCardPdf = async (clubName, opts = {}) => {
  const { seasonId, memberId, squadId } = opts;
  const slug = (clubName || 'club').toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40);
  const params = {};
  if (seasonId) params.season_id = seasonId;
  if (memberId) params.member_id = memberId;
  if (squadId)  params.squad_id  = squadId;
  const suffix = memberId ? 'points_card' : 'points_cards';
  await downloadExportBlob(
    'points-card-pdf',
    params,
    `lapistics_${slug}_${suffix}.pdf`
  );
};

// ───────────────────────────────────────────────
// SEASON LEADERBOARD
// ───────────────────────────────────────────────
export const getSeasonLeaderboard = (clubId, params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
  ).toString();
  return API.get(`/clubs/${clubId}/season-leaderboard/${qs ? '?' + qs : ''}`).then(r => r.data);
};

export const updateLeaderboardSettings = (clubId, data) =>
  API.put(`/clubs/${clubId}/leaderboard-settings/`, data).then(r => r.data);

// ─────────────────────────────────────────
// LATE ENTRIES (post-heat-generation)
// ─────────────────────────────────────────
export const addLateEntryToHeat = (heatId, data) =>
  API.post(`/heats/${heatId}/add-late-entry/`, data).then(r => r.data);

// Manual override: place a swimmer (who already has a SwimmerEntry for this
// category) into a specific empty lane in a PENDING heat. Distinct from
// addLateEntryToHeat above, which also creates the SwimmerEntry itself.
export const placeSwimmerInLane = (heatId, data) =>
  API.post(`/heats/${heatId}/place-swimmer/`, data).then(r => r.data);

export const calculateHandicaps = (heatId) =>
  API.post(`/heats/${heatId}/calculate-handicaps/`).then(r => r.data);

// Manual per-lane offset override — lets an admin key in a starting offset
// from paper/spreadsheet records when a swimmer has no PB/average on file yet
// (e.g. a club's very first handicap race). Pass offsetSeconds=null to clear
// the override and revert that lane to normal auto-calculation.
export const setManualHandicapOffset = (heatId, laneNumber, offsetSeconds) =>
  API.post(`/heats/${heatId}/set-manual-offset/`, {
    heat_id: heatId, lane_number: laneNumber, offset_seconds: offsetSeconds,
  }).then(r => r.data);

// Admin: server-anchors the synced handicap start sequence for an ACTIVE
// heat whose offsets are already calculated. Safe to call again on the same
// heat to re-anchor (e.g. after a false start) — it just overwrites the
// previous timestamp. See backend/routers/heats.py::begin_start_sequence.
export const beginStartSequence = (heatId) =>
  API.post(`/heats/${heatId}/begin-start-sequence/`).then(r => r.data);

export const recalcAgeGroups = (clubId) =>
  API.post(`/clubs/${clubId}/recalc-age-groups/`).then(r => r.data);

export const cloneChampionship = (clubId, champId, payload) =>
  API.post(`/clubs/${clubId}/championships/${champId}/clone/`, payload).then(r => r.data);

export const getAuditLogs = (clubId, params) =>
  API.get(`/clubs/${clubId}/audit-logs/`, { params }).then(r => r.data);

// ────────────────────────────────
// INTERCLUB RESULTS (Session 105)
// Reference log for results swum at other clubs' meets — read for any
// authenticated club member, write is admin-only (enforced server-side).
// ────────────────────────────────
export const getInterclubResults = (clubId) =>
  API.get(`/clubs/${clubId}/interclub-results/`).then(r => r.data);

export const createInterclubResult = (data) =>
  API.post('/interclub-results/', data).then(r => r.data);

export const updateInterclubResult = (id, data) =>
  API.put(`/interclub-results/${id}/`, data).then(r => r.data);

export const deleteInterclubResult = (id) =>
  API.delete(`/interclub-results/${id}/`).then(r => r.data);

// ────────────────────────────────
// CHAMPIONSHIP ENTRIES BULK IMPORT/EXPORT (D7)
// Bridge feature for clubs running entries off a spreadsheet/paper card
// system — see DECISIONS.md D7 and championship_entries_import_export.py.
// ────────────────────────────────
export const downloadEntriesImportTemplate = async (clubId, champId) => {
  const res = await API.get(
    `/championships/${champId}/entries/import/template/?club_id=${clubId}`,
    { responseType: 'blob' }
  );
  const objectUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `lapistics_entries_import_template.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

export const previewEntriesImport = async (clubId, champId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await API.post(
    `/championships/${champId}/entries/import/preview/?club_id=${clubId}`,
    formData
  );
  return res.data;
};

export const commitEntriesImport = async (clubId, champId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await API.post(
    `/championships/${champId}/entries/import/commit/?club_id=${clubId}`,
    formData
  );
  return res.data;
};

export const downloadEntriesExport = async (clubId, champId, champName) => {
  const res = await API.get(
    `/championships/${champId}/entries/export/?club_id=${clubId}`,
    { responseType: 'blob' }
  );
  const objectUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = objectUrl;
  const slug = (champName || 'championship').toLowerCase().replace(/\s+/g, '_').slice(0, 30);
  a.download = `lapistics_${slug}_entries_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

// ────────────────────────
// RELAY TEAMS (DECISIONS.md D16, Session 163: frontend team-builder)
// ────────────────────────
export const getEventRelayTeams = (eventId, raceCategoryId = null) => {
  const qs = raceCategoryId ? `?race_category_id=${raceCategoryId}` : '';
  return API.get(`/events/${eventId}/relay-teams/${qs}`).then(r => r.data);
};

export const getRelayTeam = (teamId) =>
  API.get(`/relay-teams/${teamId}/`).then(r => r.data);

export const createRelayTeam = (data) =>
  API.post('/relay-teams/', data).then(r => r.data);

export const updateRelayTeamMembers = (data) =>
  API.put('/relay-teams/members/', data).then(r => r.data);

export const deleteRelayTeam = (teamId) =>
  API.delete(`/relay-teams/${teamId}/`).then(r => r.data);

// ────────────────────────
// ADVANCE TO FINAL (DECISIONS.md D12, open item #2)
// Takes the top N finishers/teams from a completed round (e.g. Heats) and
// auto-enters them into a Final round. See backend/routers/events.py::advance_to_final.
// ────────────────────────
export const advanceToFinal = (eventId, raceCategoryId, data) =>
  API.post(`/events/${eventId}/categories/${raceCategoryId}/advance-to-final/`, data).then(r => r.data);

export default API;
